import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TickDouble02Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  InputSize,
  Show
} from "shared/components";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { Pagination } from "shared/components/Pagination";
import { ActivityRow } from "./ActivityRow";
import { groupByDate } from "shared/utils/group-by-date";
import { cx } from "shared/utils";
import {
  ActivityCategory,
  UnitActivityRow,
  useUnitActivityLog
} from "./controller";

const CATEGORY_LABELS: { value: ActivityCategory | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "leads", label: "Leads" },
  { value: "showings", label: "Showings" },
  { value: "applications", label: "Applications" },
  { value: "messages", label: "Messages" },
  { value: "details", label: "Details" },
  { value: "pms_sync", label: "PMS sync" }
];

const navigateTarget = (row: UnitActivityRow) => {
  const target = row.navigate_to;
  if (!target) return null;

  switch (target.type) {
    case "inbox":
      return `/inbox?c=${target.lead_id}`;
    case "showing":
      return `/showings?lead_id=${target.lead_id}`;
    default:
      return `/leads/${target.lead_id}`;
  }
};

const CategoryTabLabel = ({
  label,
  count,
  active
}: {
  label: string;
  count: number;
  active: boolean;
}) => (
  <span className="inline-flex items-center gap-1">
    {label}
    <span
      className={cx(
        "inline-flex items-center rounded-md px-1.5 py-0.5 text-[13px] font-medium",
        active
          ? "bg-primary-500 text-white"
          : "border border-alpha-80 bg-white text-content-1000 shadow-xs"
      )}
    >
      {count}
    </span>
  </span>
);

export const ActivityLogTab = ({
  propertyId,
  unitId
}: {
  propertyId?: number;
  unitId?: number;
}) => {
  const navigate = useNavigate();
  const {
    rows,
    categoryCounts,
    unreadCount,
    pageCount,
    page,
    setPage,
    category,
    setCategory,
    search,
    setSearch,
    isFetching,
    markAllRead,
    markRowRead
  } = useUnitActivityLog({ propertyId, unitId });

  const groups = useMemo(
    () => groupByDate(rows, (row) => row.occurred_at),
    [rows]
  );

  const tabs = useMemo<Tab<ActivityCategory | "all">[]>(
    () =>
      CATEGORY_LABELS.map(({ value, label }) => ({
        value,
        label: (
          <CategoryTabLabel
            label={label}
            count={categoryCounts[value] ?? 0}
            active={value === category}
          />
        )
      })),
    [categoryCounts, category]
  );

  const handleSelect = (row: UnitActivityRow) => {
    if (row.activity_log_id) markRowRead.mutate(row.activity_log_id);

    const target = navigateTarget(row);
    if (target) navigate(target);
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <BaseInput
          id="activity-search"
          autoComplete="off"
          placeholder="Search activity, lead name, property..."
          value={search}
          onChange={setSearch}
          size={InputSize.SM}
          className="!mb-0 max-w-[361px] w-full"
        />

        <div className="max-w-full overflow-x-auto">
          <div className="w-fit">
            <Tabs<ActivityCategory | "all">
              tabStyle="outline"
              size="sm"
              active={category}
              onChange={setCategory}
              tabs={tabs}
              fullWidthTabs={false}
            />
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <Show if={unreadCount > 0}>
            <span className="text-xs font-bold text-content-1000">
              {unreadCount} Unread
            </span>
          </Show>
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            HugeIcon={TickDouble02Icon}
            disabled={unreadCount === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark as read
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <Show if={isFetching && !rows.length}>
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        </Show>

        <Show if={!isFetching && !rows.length}>
          <NoRowsFallback
            title="No activity yet"
            text="Activity for this unit will appear here"
          />
        </Show>

        {groups.map((group) => (
          <div key={group.name}>
            <p className="border-b border-alpha-40 bg-background-900 px-6 py-3 text-xs font-bold text-content-500">
              {group.name}
            </p>
            {group.items.map((row) => (
              <ActivityRow key={row.id} row={row} onSelect={handleSelect} />
            ))}
          </div>
        ))}
      </Card>

      <Show if={pageCount > 1}>
        <Pagination
          className="justify-center md:justify-end"
          total={pageCount}
          current={page}
          onChange={setPage}
        />
      </Show>
    </div>
  );
};
